import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, Observable, of, OperatorFunction } from 'rxjs';
import { catchError, finalize, map, retry, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import {
  Invitation,
  InvitationAccessLevel,
  InvitationsApiPicker,
  InvitationStatusType,
  ListResponse,
} from '@vi-iot/client';

import {
  CommercialActions,
  CommercialStore,
  FetchedPartnerAccessInvitation,
  InstallationDetailsStore,
  RequestsArrayStatus,
  ResponseStatus,
} from '@store';
import { ViToastService } from '../../../projects/ui';
import { UIService } from '../../shared/services';

import { CommercialMappers } from './commercial.mappers';

@Injectable()
export class CommercialEffects {
  fetchPartnerAccessList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        CommercialActions.fetchPartnerAccessList.attempt,
        CommercialActions.updateVisibilityOfPartnerAccessInvitation.success
      ),
      concatLatestFrom(() => [this.installationDetailsStore.getInstallationId$]),
      switchMap(([_action, installationId]) =>
        this.invitationsApi
          .instance()
          .listInvitations({ limit: 1000, installationId }, 'v2')
          .pipe(
            this.fetchInvitationsRecursively(installationId),
            map((invitations) => {
              return CommercialActions.fetchPartnerAccessList.success({ invitations });
            }),
            catchError(() => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.INSTALLATION.DEVICE_CARD.SHARE_DEVICE_MONITORING.FETCH_ERROR'
                ),
              });

              return of(CommercialActions.fetchPartnerAccessList.error());
            }),
            takeUntil(this.actions$.pipe(ofType(CommercialActions.fetchPartnerAccessList.cancel)))
          )
      )
    );
  });

  // handle all request in effect to make sure UI blocking is correct and separated from component
  sendNewInvitations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommercialActions.sendPartnerAccessInvitations.sendAll),
      concatLatestFrom(() => [
        this.store$.invitationsToSend$,
        this.installationDetailsStore.getInstallationId$,
      ]),
      tap(() => {
        this.uiService.blockUI();
      }),
      map(([_action, invitations, installationId]) => {
        const actions: Observable<RequestsArrayStatus>[] = [];
        invitations.forEach((invitation, index) => {
          if (invitation.responseStatus === ResponseStatus.Loading) {
            actions.push(
              this.invitationsApi
                .instance()
                .createInvitation({
                  accessLevel: invitation.accessLevel,
                  inviteeEmail: invitation.inviteeEmail,
                  installationId,
                })
                .pipe(
                  map(() => ({
                    status: ResponseStatus.Success,
                    index,
                  })),
                  retry(3),
                  catchError(() => {
                    const output: RequestsArrayStatus = {
                      status: ResponseStatus.Error,
                      index,
                    };

                    return of(output);
                  })
                )
            );
          }
        });

        return forkJoin(actions);
      }),
      switchMap((responses) => responses),
      switchMap((responses) => {
        let isAnyInvitationSuccessful = false;

        const actions = responses.map((response) => {
          if (response.status === ResponseStatus.Error) {
            return CommercialActions.sendPartnerAccessInvitations.error({ index: response.index });
          }

          if (response.status === ResponseStatus.Success) {
            isAnyInvitationSuccessful = true;

            return CommercialActions.sendPartnerAccessInvitations.success({
              index: response.index,
            });
          }
        });

        this.uiService.unblockUI();
        if (isAnyInvitationSuccessful) {
          this.store$.fetchPartnerAccessList();
        }

        return actions;
      })
    );
  });

  removeInvitation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommercialActions.removePartnerAccessInvitation.attempt),
      tap(() => {
        this.uiService.blockUI();
      }),
      switchMap((action) => {
        const status = CommercialMappers.getProperStatusOnInvitationRemoval(action.status);

        return this.invitationsApi
          .instance()
          .updateInvitation(action.token, { status })
          .pipe(
            retry(3),
            finalize(() => {
              this.uiService.unblockUI();
            }),
            map(() =>
              CommercialActions.removePartnerAccessInvitation.success({ token: action.token })
            ),
            catchError(() => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.INSTALLATION.DEVICE_CARD.SHARE_DEVICE_MONITORING.REMOVE_ERROR'
                ),
              });

              return of(CommercialActions.removePartnerAccessInvitation.error());
            })
          );
      })
    );
  });

  hideInvitation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommercialActions.updateVisibilityOfPartnerAccessInvitation.attempt),
      tap(() => {
        this.uiService.blockUI();
      }),
      switchMap((action) => {
        return this.invitationsApi
          .instance()
          .updateInvitationVisibility(action.token, action.isVisible)
          .pipe(
            retry(3),
            finalize(() => {
              this.uiService.unblockUI();
            }),
            map(() =>
              CommercialActions.updateVisibilityOfPartnerAccessInvitation.success({
                token: action.token,
              })
            ),
            catchError(() => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.INSTALLATION.DEVICE_CARD.SHARE_DEVICE_MONITORING.REMOVE_ERROR'
                ),
              });

              return of(CommercialActions.updateVisibilityOfPartnerAccessInvitation.error());
            })
          );
      })
    );
  });

  // manual retry
  sendSpecificPartnerAccessInvitation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommercialActions.sendPartnerAccessInvitations.sendOne),
      concatLatestFrom((action) => [
        this.store$.getInvitationWithIndex(action.index),
        this.installationDetailsStore.getInstallationId$,
      ]),
      tap(() => {
        this.uiService.blockUI();
      }),
      switchMap(([action, { accessLevel, inviteeEmail }, installationId]) =>
        this.invitationsApi
          .instance()
          .createInvitation({
            accessLevel,
            inviteeEmail,
            installationId,
          })
          .pipe(
            retry(3),
            finalize(() => {
              this.uiService.unblockUI();
            }),
            switchMap(() => [
              CommercialActions.sendPartnerAccessInvitations.success({ index: action.index }),
              CommercialActions.fetchPartnerAccessList.attempt(),
            ]),
            catchError(() => {
              return of(
                CommercialActions.sendPartnerAccessInvitations.error({ index: action.index })
              );
            })
          )
      )
    );
  });

  constructor(
    private store$: CommercialStore,
    private actions$: Actions,
    private invitationsApi: InvitationsApiPicker,
    private installationDetailsStore: InstallationDetailsStore,
    private toastService: ViToastService,
    private translateService: TranslateService,
    private uiService: UIService
  ) {}

  // Very unlikely but possible - handle case when installation reach more than 1k invitations
  private fetchInvitationsRecursively(
    installationId: number
  ): OperatorFunction<ListResponse<Invitation>, FetchedPartnerAccessInvitation[]> {
    return (
      inputObservable: Observable<ListResponse<Invitation>>
    ): Observable<FetchedPartnerAccessInvitation[]> =>
      inputObservable.pipe(
        switchMap((res) => {
          if (res.paging?.next) {
            const offset = res.paging.offset + 1000;

            return this.invitationsApi
              .instance()
              .listInvitations({ installationId, limit: 1000, offset }, 'v2')
              .pipe(
                switchMap((nextRes) =>
                  forkJoin([
                    of(res.data),
                    of(nextRes).pipe(this.fetchInvitationsRecursively(installationId)),
                  ]).pipe(
                    map(([previousRequest, latestRequest]) => [
                      ...this.filterAndMapAccessDataFromInvitation(previousRequest),
                      ...latestRequest,
                    ])
                  )
                )
              );
          }

          // latestRequest
          return of(this.filterAndMapAccessDataFromInvitation(res.data));
        })
      );
  }

  private filterAndMapAccessDataFromInvitation(
    invitations: Invitation[]
  ): FetchedPartnerAccessInvitation[] {
    return invitations.reduce((acc: FetchedPartnerAccessInvitation[], invitation) => {
      // check if included in CommercialInvitationAccessLevel

      const partnerAccessLevel =
        invitation.accessLevel === InvitationAccessLevel.Partner ||
        invitation.accessLevel === InvitationAccessLevel.Consumer
          ? invitation.accessLevel
          : undefined;

      const ignorednvitationTypes = [
        InvitationStatusType.Canceled,
        InvitationStatusType.Terminated,
      ];
      const isProperStatus = ignorednvitationTypes.every((status) => status !== invitation.status);
      const isInviteeName = invitation.invitee?.firstName && invitation.invitee?.lastName;

      if (partnerAccessLevel && isProperStatus) {
        return [
          ...acc,
          {
            token: invitation.token,
            tokenType: invitation.tokenType,
            tokenExpirationDate: invitation.tokenExpirationDate,
            inviteeEmail: invitation.inviteeEmail,
            accessLevel: partnerAccessLevel,
            status: invitation.status,
            companyName: invitation.invitee?.companyName,
            userName: isInviteeName
              ? `${invitation.invitee?.firstName} ${invitation.invitee?.lastName}`
              : null,
            inviter: invitation.inviter,
            invitee: invitation.invitee,
            customerNumber: invitation.customerNumber,
            ownedByMaintainer: invitation.ownedByMaintainer,
            isVisible: invitation.isVisible,
          },
        ];
      }

      return acc;
    }, []);
  }
}
